import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Send, 
  Users,
  MapPin,
  MessageSquare
} from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { fr } from 'date-fns/locale';
import BottomNav from '@/components/BottomNav';

const ChatPage = () => {
  const navigate = useNavigate();
  const { roomId } = useParams();
  const { user, api } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [currentRoom, setCurrentRoom] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (roomId) {
      fetchMessages();
      const room = rooms.find(r => r.room_id === roomId);
      setCurrentRoom(room);
    } else {
      fetchRooms();
    }
  }, [roomId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchRooms = async () => {
    try {
      setLoading(true);
      const response = await api.get('/chat/rooms');
      setRooms(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des salons');
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/chat/rooms/${roomId}/messages`);
      setMessages(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des messages');
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!newMessage.trim()) return;

    setSending(true);
    try {
      const response = await api.post(`/chat/rooms/${roomId}/messages`, {
        content: newMessage.trim()
      });
      setMessages([...messages, response.data]);
      setNewMessage('');
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  const handleJoinRoom = async (room) => {
    try {
      await api.post(`/chat/rooms/${room.room_id}/join`);
      navigate(`/chat/${room.room_id}`);
    } catch (error) {
      toast.error('Erreur lors de la connexion au salon');
    }
  };

  // Room List View
  if (!roomId) {
    return (
      <div className="min-h-screen tropic-bg pb-28 md:pb-12 md:pt-16">
        <header className="sticky top-0 md:top-16 z-40 glass border-b">
          <div className="container mx-auto max-w-2xl px-4 py-4">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate(-1)}
                data-testid="back-btn"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold">Chat de Quartier</h1>
                <p className="text-xs text-muted-foreground">Discutez avec vos voisins</p>
              </div>
            </div>
          </div>
        </header>

        <main className="container mx-auto max-w-2xl px-4 py-6">
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="skeleton h-24 rounded-xl" />
              ))}
            </div>
          ) : rooms.length === 0 ? (
            <div className="text-center py-16">
              <Users className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-xl font-bold mb-2">Aucun salon disponible</h3>
              <p className="text-muted-foreground">
                Les salons de discussion seront bientôt disponibles
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {rooms.map((room) => (
                <Card 
                  key={room.room_id}
                  className="cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => handleJoinRoom(room)}
                  data-testid={`chat-room-${room.room_id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                        <MapPin className="h-6 w-6 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <h3 className="font-bold">{room.name}</h3>
                          <Badge variant="outline" className="text-xs">
                            <Users className="h-3 w-3 mr-1" />
                            {room.members_count}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          {room.description}
                        </p>
                        {room.last_message && (
                          <p className="text-xs text-muted-foreground mt-2 truncate">
                            {room.last_message}
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </main>

        <BottomNav />
      </div>
    );
  }

  // Chat View
  return (
    <div className="min-h-screen tropic-bg flex flex-col">
      {/* Header */}
      <header className="sticky top-0 md:top-16 z-40 glass border-b">
        <div className="container mx-auto max-w-2xl px-4 py-4">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate('/chat')}
              data-testid="back-to-rooms-btn"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex-1">
              <h1 className="font-bold">{currentRoom?.name || 'Chat'}</h1>
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <Users className="h-3 w-3" />
                {currentRoom?.members_count || 0} membres
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Messages */}
      <ScrollArea className="flex-1 px-4 py-4">
        <div className="container mx-auto max-w-2xl space-y-4">
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className={`skeleton h-16 rounded-xl ${i % 2 === 0 ? 'w-3/4' : 'w-3/4 ml-auto'}`} />
              ))}
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-16">
              <MessageSquare className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Soyez le premier à écrire !</p>
            </div>
          ) : (
            messages.map((msg, index) => {
              const isOwnMessage = msg.user_id === user?.user_id;
              const showDate = index === 0 || 
                new Date(messages[index - 1].created_at).toDateString() !== new Date(msg.created_at).toDateString();
              
              return (
                <div key={msg.message_id}>
                  {showDate && (
                    <div className="text-center my-4">
                      <span className="text-xs bg-muted px-3 py-1 rounded-full text-muted-foreground">
                        {format(new Date(msg.created_at), 'd MMMM', { locale: fr })}
                      </span>
                    </div>
                  )}
                  <div className={`flex gap-2 ${isOwnMessage ? 'flex-row-reverse' : ''}`}>
                    {!isOwnMessage && (
                      <Avatar className="h-8 w-8 flex-shrink-0">
                        <AvatarImage src={msg.user_picture} />
                        <AvatarFallback className="text-xs">{msg.user_name?.charAt(0)}</AvatarFallback>
                      </Avatar>
                    )}
                    <div className={`max-w-[75%] ${isOwnMessage ? 'items-end' : 'items-start'}`}>
                      {!isOwnMessage && (
                        <p className="text-xs font-medium mb-1 ml-1">{msg.user_name}</p>
                      )}
                      <div 
                        className={`rounded-2xl px-4 py-2 ${
                          isOwnMessage 
                            ? 'bg-primary text-primary-foreground rounded-br-sm' 
                            : 'bg-muted rounded-bl-sm'
                        }`}
                      >
                        <p className="text-sm">{msg.content}</p>
                      </div>
                      <p className={`text-xs text-muted-foreground mt-1 ${isOwnMessage ? 'text-right mr-1' : 'ml-1'}`}>
                        {format(new Date(msg.created_at), 'HH:mm', { locale: fr })}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>

      {/* Input */}
      <div className="sticky bottom-0 glass border-t border-border/50 p-4">
        <div className="container mx-auto max-w-2xl">
          <div className="flex gap-2">
            <Input
              placeholder="Votre message..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
              disabled={sending}
              className="flex-1"
              data-testid="chat-message-input"
            />
            <Button 
              onClick={handleSend}
              disabled={!newMessage.trim() || sending}
              data-testid="chat-send-btn"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatPage;
